import {
    ArrowLeftIcon,
    BookOpenIcon,
    CalendarDaysIcon,
    ChatBubbleLeftRightIcon,
    ChevronRightIcon,
    ClipboardDocumentListIcon,
    ClockIcon,
    MapIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'

import FamilyNotesSection from './FamilyNotesSection'
import InfoTip from './InfoTip'
import { HomeworkItem } from './ProviderFamilyHomework'
import { useProviderSession } from './ProviderSession'

type ClientStatus = 'active' | 'onboarding' | 'at_risk'
type EngagementLevel = 'high' | 'medium' | 'low' | 'insufficient'

interface FamilyDetailResponse {
    licenseId: string
    email: string
    parentName: string | null
    childName?: string
    consentToShareDetail: boolean
    status: ClientStatus
    engagementLevel: EngagementLevel
    lastActiveAt: string | null
    lastAppointment: string | null
    upcomingAppointment: string | null
    openHomework: number
    engagement?: {
        messagesThisWeek: number
        messagesTotal: number
        milestonesCompleted: number
        biteSizeStepsCompleted: number
        resourcesSaved: number
    }
    engagementByWeek?: { weekStart: string; score: number }[]
}

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const STATUS_BADGE: Record<ClientStatus, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-green-50 text-green-700 border-green-200' },
    onboarding: { label: 'Onboarding', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    at_risk: { label: 'At risk', cls: 'bg-red-50 text-red-700 border-red-200' },
}

const ENGAGEMENT_LABEL: Record<EngagementLevel, string> = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    insufficient: 'Not enough data yet',
}

export default function ProviderFamilyDetail() {
    const { id } = useParams()
    const session = useProviderSession()
    const canManage = session?.role !== 'viewer'
    const [detail, setDetail] = useState<FamilyDetailResponse | null>(null)
    const [homework, setHomework] = useState<HomeworkItem[]>([])
    const [showHomework, setShowHomework] = useState(false)
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)

    useEffect(() => {
        let cancelled = false
        async function load() {
            const [response, hw] = await Promise.all([
                api<FamilyDetailResponse>(`${ROUTES.provider.family}/${id}`),
                api<{ items: HomeworkItem[] }>(`${ROUTES.provider.homework}/${id}`),
            ])
            if (cancelled) return
            if (response.status && response.status < 400 && response.data) {
                setDetail(response.data)
            } else {
                setNotFound(true)
            }
            if (hw.status && hw.status < 400 && hw.data) {
                setHomework(hw.data.items)
            }
            setLoading(false)
        }
        if (id) load()
        return () => {
            cancelled = true
        }
    }, [id])

    if (loading) {
        return <div className='max-w-5xl mx-auto px-4 py-12 text-center text-sm text-neutral-500'>Loading…</div>
    }

    if (notFound || !detail) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12 text-center'>
                <p className='text-neutral-600'>Client not found.</p>
                <Link to='/clients' className='text-brand-600 hover:underline'>
                    Back to clients
                </Link>
            </div>
        )
    }

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to='/clients'
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                All clients
            </Link>

            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6'>
                <div>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>
                        {detail.childName ?? detail.email}
                    </h1>
                    <p className='text-sm text-neutral-600 mt-1'>
                        {detail.parentName ? `Parent ${detail.parentName} · ` : ''}
                        {detail.email}
                    </p>
                </div>
                <div className='flex items-center gap-2'>
                    <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${STATUS_BADGE[detail.status].cls}`}
                    >
                        {STATUS_BADGE[detail.status].label}
                        <InfoTip text='Active: engaging regularly. Onboarding: still completing setup and early milestones. At risk: no meaningful app activity in 30+ days, stalled roadmap progress, or incomplete onboarding.' />
                    </span>
                    <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border border-neutral-200 bg-neutral-50 text-neutral-700'>
                        {detail.engagementLevel === 'insufficient'
                            ? ENGAGEMENT_LABEL.insufficient
                            : `${ENGAGEMENT_LABEL[detail.engagementLevel]} engagement`}
                        <InfoTip
                            text={
                                detail.engagementLevel === 'insufficient'
                                    ? 'This family was invited within the last 72 hours. Engagement is not rated until there is enough activity to judge.'
                                    : 'A High / Medium / Low rating from recent app activity (logins, roadmap steps, homework, saved resources, and chat) relative to the rest of the caseload.'
                            }
                        />
                    </span>
                </div>
            </div>

            {/* Appointments */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6'>
                <Appointment
                    label='Last appointment'
                    value={formatDate(detail.lastAppointment) ?? 'No record'}
                    emphasis={false}
                />
                <Appointment
                    label='Upcoming appointment'
                    value={formatDate(detail.upcomingAppointment) ?? 'None scheduled'}
                    emphasis={!detail.upcomingAppointment}
                />
            </div>

            {detail.engagement && (
                <>
                    {/* Engagement KPIs */}
                    <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
                        <Stat
                            icon={ClockIcon}
                            label='Last active'
                            value={formatDate(detail.lastActiveAt) ?? 'Never'}
                            sub='in the client app'
                        />
                        <Stat
                            icon={ChatBubbleLeftRightIcon}
                            label='Chat messages this week'
                            value={String(detail.engagement.messagesThisWeek)}
                            sub={`${detail.engagement.messagesTotal} total`}
                        />
                        <Stat
                            icon={MapIcon}
                            label='Roadmap milestones'
                            value={String(detail.engagement.milestonesCompleted)}
                            sub='completed'
                        />
                        <Stat
                            icon={BookOpenIcon}
                            label='Resources saved'
                            value={String(detail.engagement.resourcesSaved)}
                            sub={`${detail.engagement.biteSizeStepsCompleted} bite-size steps done`}
                        />
                    </div>

                    {/* Homework — surfaced above the trend; highlighted when items are open */}
                    <button
                        onClick={() => setShowHomework(true)}
                        className={`w-full flex items-center gap-4 border rounded-2xl p-5 mb-6 text-left transition-colors ${
                            detail.openHomework > 0
                                ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                                : 'bg-white border-neutral-200 hover:border-brand-300'
                        }`}
                    >
                        <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                detail.openHomework > 0 ? 'bg-amber-100' : 'bg-brand-50'
                            }`}
                        >
                            <ClipboardDocumentListIcon
                                className={`w-5 h-5 ${detail.openHomework > 0 ? 'text-amber-700' : 'text-brand-600'}`}
                            />
                        </div>
                        <div className='flex-1 min-w-0'>
                            <div className='flex items-center gap-2'>
                                <h2 className='text-base font-semibold text-neutral-900'>Homework</h2>
                                {detail.openHomework > 0 && (
                                    <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200'>
                                        {detail.openHomework} open
                                    </span>
                                )}
                            </div>
                            <p className='text-sm text-neutral-600'>
                                {detail.openHomework === 0
                                    ? 'No open homework. View homework history.'
                                    : `${detail.openHomework} open item${detail.openHomework === 1 ? '' : 's'} for the client to complete. View details.`}
                            </p>
                        </div>
                        <ChevronRightIcon className='w-5 h-5 text-neutral-400 flex-shrink-0' />
                    </button>

                    {canManage && (
                        <div className='-mt-4 mb-6'>
                            <Link
                                to={`/clients/${id}/homework`}
                                className='inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700'
                            >
                                <ClipboardDocumentListIcon className='w-4 h-4' />
                                Manage or assign homework
                            </Link>
                        </div>
                    )}

                    {/* Client engagement trend */}
                    <div className='bg-white border border-neutral-200 rounded-2xl p-6 mb-6'>
                        <h2 className='text-lg font-semibold text-neutral-900 mb-1'>Client engagement trend</h2>
                        <p className='text-sm text-neutral-600 mb-4'>
                            Weekly engagement across logins, roadmap steps, homework, saved resources, and chat.
                            Message content is never shared.
                        </p>
                        <TrendChart weeks={detail.engagementByWeek ?? []} childName={detail.childName} />
                    </div>
                </>
            )}

            {/* Coordinator notes are org-internal record-keeping. */}
            <FamilyNotesSection licenseId={id!} />

            {showHomework && (
                <HomeworkModal
                    childName={detail.childName ?? detail.email}
                    items={homework}
                    manageHref={canManage ? `/clients/${id}/homework` : undefined}
                    onClose={() => setShowHomework(false)}
                />
            )}
        </div>
    )
}

// Overdue is derived on read so it never goes stale (it is not a stored status).
type DisplayStatus = 'open' | 'overdue' | 'completed'

function displayStatus(item: HomeworkItem): DisplayStatus {
    if (item.status === 'completed') return 'completed'
    if (item.dueDate && new Date(item.dueDate).valueOf() < Date.now()) return 'overdue'
    return 'open'
}

function HomeworkModal({
    childName,
    items,
    manageHref,
    onClose,
}: {
    childName: string
    items: HomeworkItem[]
    manageHref?: string
    onClose: () => void
}) {
    // Cancelled items are already excluded server-side; sort open/overdue first.
    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50' onClick={onClose}>
            <div
                className='bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col'
                onClick={(e) => e.stopPropagation()}
            >
                <div className='flex items-center justify-between p-5 border-b border-neutral-100'>
                    <div>
                        <h2 className='text-lg font-semibold text-neutral-900'>Homework</h2>
                        <p className='text-sm text-neutral-500'>{childName}</p>
                    </div>
                    <button onClick={onClose} className='p-1 text-neutral-400 hover:text-neutral-700'>
                        <XMarkIcon className='w-5 h-5' />
                    </button>
                </div>
                <div className='p-5 overflow-y-auto space-y-3'>
                    {items.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No homework has been assigned yet.</p>
                    ) : (
                        items.map((hw) => {
                            const status = displayStatus(hw)
                            return (
                                <div key={hw._id} className='border border-neutral-200 rounded-xl p-4'>
                                    <div className='flex items-start justify-between gap-3'>
                                        <h3 className='text-sm font-semibold text-neutral-900'>{hw.title}</h3>
                                        <HomeworkStatusBadge status={status} />
                                    </div>
                                    {hw.description && (
                                        <p className='text-sm text-neutral-600 mt-1 whitespace-pre-wrap'>{hw.description}</p>
                                    )}
                                    <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 mt-2'>
                                        {status === 'completed'
                                            ? hw.completedAt && <span>Completed {formatDate(hw.completedAt)}</span>
                                            : hw.dueDate && <span>Due {formatDate(hw.dueDate)}</span>}
                                        {hw.assignedByName && <span>Assigned by {hw.assignedByName}</span>}
                                    </div>
                                    {hw.completionNote && (
                                        <div className='mt-2 text-xs text-neutral-600 bg-neutral-50 border border-neutral-100 rounded-lg p-2 whitespace-pre-wrap'>
                                            &ldquo;{hw.completionNote}&rdquo;
                                        </div>
                                    )}
                                </div>
                            )
                        })
                    )}
                </div>
                {manageHref && (
                    <div className='p-4 border-t border-neutral-100'>
                        <Link
                            to={manageHref}
                            onClick={onClose}
                            className='inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700'
                        >
                            <ClipboardDocumentListIcon className='w-4 h-4' />
                            Manage or assign homework
                        </Link>
                    </div>
                )}
            </div>
        </div>
    )
}

function HomeworkStatusBadge({ status }: { status: DisplayStatus }) {
    const map: Record<DisplayStatus, { label: string; cls: string }> = {
        open: { label: 'Open', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
        overdue: { label: 'Overdue', cls: 'bg-red-50 text-red-700 border-red-200' },
        completed: { label: 'Completed', cls: 'bg-green-50 text-green-700 border-green-200' },
    }
    const s = map[status]
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${s.cls}`}
        >
            {s.label}
        </span>
    )
}

function Appointment({ label, value, emphasis }: { label: string; value: string; emphasis: boolean }) {
    return (
        <div
            className={`rounded-xl border p-4 ${emphasis ? 'border-amber-200 bg-amber-50' : 'border-neutral-200 bg-white'}`}
        >
            <div className='flex items-center gap-2 mb-2'>
                <CalendarDaysIcon className={`w-4 h-4 ${emphasis ? 'text-amber-600' : 'text-brand-600'}`} />
                <span className='text-xs uppercase tracking-wider text-neutral-500'>{label}</span>
            </div>
            <div className={`text-lg font-semibold ${emphasis ? 'text-amber-800' : 'text-neutral-900'}`}>{value}</div>
        </div>
    )
}

function Stat({
    icon: Icon,
    label,
    value,
    sub,
}: {
    icon: React.ComponentType<{ className?: string }>
    label: string
    value: string
    sub: string
}) {
    return (
        <div className='bg-white rounded-xl border border-neutral-200 p-4'>
            <div className='flex items-center gap-2 mb-2'>
                <Icon className='w-4 h-4 text-brand-600' />
                <span className='text-xs uppercase tracking-wider text-neutral-500'>{label}</span>
            </div>
            <div className='text-xl font-bold text-neutral-900'>{value}</div>
            <div className='text-xs text-neutral-500 mt-0.5'>{sub}</div>
        </div>
    )
}

function formatWeek(iso: string): string {
    const d = new Date(iso)
    // weekStart is a UTC Monday; format in UTC so the label doesn't slip a day.
    return isNaN(d.valueOf()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function TrendChart({ weeks, childName }: { weeks: { weekStart: string; score: number }[]; childName?: string | null }) {
    const who = childName || 'this client'
    // Graceful degradation for new / inactive clients. Without these, a brand-new
    // client renders as a flat line pinned to zero with one mystery spike, which
    // reads as a broken chart rather than "no data yet".
    if (weeks.length < 2) {
        return (
            <div className='rounded-xl border border-dashed border-neutral-200 bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-500'>
                Not enough history yet to show a weekly trend. It builds up as {who} uses the app.
            </div>
        )
    }
    const points = weeks.map((w) => w.score)
    const total = points.reduce((a, b) => a + b, 0)
    if (total === 0) {
        return (
            <div className='rounded-xl border border-dashed border-neutral-200 bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-500'>
                No engagement recorded yet. This chart fills in as {who} logs in and works through their roadmap,
                homework, and resources.
            </div>
        )
    }

    const w = 600
    const h = 120
    const max = Math.max(...points, 1)
    const stepX = w / (points.length - 1 || 1)
    const coords = points.map((p, i) => ({ x: i * stepX, y: h - (p / max) * h }))
    const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')
    const area = `${path} L${w},${h} L0,${h} Z`

    return (
        <div>
            {/* Scale + summary: without a y-axis, these give the numbers meaning. */}
            <div className='mb-2 flex items-baseline justify-between text-xs text-neutral-500'>
                <span>
                    Peak <span className='font-semibold text-neutral-700'>{max}</span>{' '}
                    {max === 1 ? 'activity' : 'activities'} in a week
                </span>
                <span>
                    {total} total across {weeks.length} weeks
                </span>
            </div>
            <svg viewBox={`0 0 ${w} ${h}`} className='w-full h-32' preserveAspectRatio='none' role='img'
                aria-label={`Weekly engagement, peak ${max} activities, ${total} total across ${weeks.length} weeks`}>
                <defs>
                    <linearGradient id='trend-fill' x1='0' x2='0' y1='0' y2='1'>
                        <stop offset='0%' stopColor='currentColor' stopOpacity='0.18' />
                        <stop offset='100%' stopColor='currentColor' stopOpacity='0' />
                    </linearGradient>
                </defs>
                {/* Zero baseline so weeks with no activity read as sitting on the floor. */}
                <line x1='0' y1={h - 0.5} x2={w} y2={h - 0.5} stroke='currentColor' strokeWidth='1'
                    className='text-neutral-200' vectorEffect='non-scaling-stroke' />
                <g className='text-brand-600'>
                    <path d={area} fill='url(#trend-fill)' />
                    {/* non-scaling-stroke keeps the line crisp despite the non-uniform x-stretch. */}
                    <path d={path} fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinejoin='round'
                        strokeLinecap='round' vectorEffect='non-scaling-stroke' />
                    {/* Full-height hover targets per week give a native tooltip with the
                        exact week + count, replacing the guesswork of a bare sparkline. */}
                    {weeks.map((wk, i) => (
                        <rect key={i} x={i === 0 ? 0 : coords[i].x - stepX / 2}
                            y='0' width={i === 0 || i === weeks.length - 1 ? stepX / 2 : stepX} height={h}
                            fill='transparent'>
                            <title>{`Week of ${formatWeek(wk.weekStart)}: ${wk.score} ${wk.score === 1 ? 'activity' : 'activities'}`}</title>
                        </rect>
                    ))}
                </g>
            </svg>
            {/* X-axis endpoints (aligned to the full-width chart edges). Per-week
                detail is available on hover; labeling every week would crowd. */}
            <div className='mt-2 flex justify-between text-xs text-neutral-400'>
                <span>Week of {formatWeek(weeks[0].weekStart)}</span>
                <span>Week of {formatWeek(weeks[weeks.length - 1].weekStart)}</span>
            </div>
        </div>
    )
}
